import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useForm, Controller } from 'react-hook-form';
import { useEffect, useState } from 'react';
import { yupResolver } from '@hookform/resolvers/yup';
import isEmpty from 'lodash.isempty';
import * as Yup from 'yup';
import i18n from '../../i18n';
import useStepPost from '../../hooks/useStepPost';
import { getValidationSchema } from '../../util/helpers/getValidationSchema';
import Paragraph from '../Paragraph/Paragraph';
import { FormWrap } from '../FormContainer/FormContainer';
import { HeadingSubtitle } from '../Heading';
import Checkbox from '../Fields/Checkbox';
import OnBoardingCta from '../OnBoarding/OnBoardingCta';
import {
  selectFundingSource,
  selectFundingPurposeSource,
} from '../../store/selectors/fundingsourceSelectors';
import themeColors from '../../assets/styles/themeColors';
import Radio from '../Fields/RadioButton';
import InputText, { ErrorMessage } from '../Fields/InputText';
import {
  getFundingSource,
  getFundingPurpose,
} from '../../store/actions/fundingSource/fundingSourceActions';
import useApplicationFundingSourceDispatch from '../../hooks/useApplicationFundingSourceDispatch';
import { selectApplication } from '../../store/selectors/applicationSelectors';
import useGtmStepHook from '../../util/hooks/useGtmHook';
import { retrieveFromSessionStorage } from '../../util/helpers/sessionStorageHelper';
import { ACCOUNT_TYPE_GTM } from '../../constants/sessionStorage';
import { gtmStepHelper } from '../../util/helpers/gtmHelper';
import { ACCOUNT_STEP_VIEW, ACCOUNT_STEP_COMPLETE } from '../../util/gtmEvents';
import { useApplicantData } from '../../hooks/useApplicantData';

const FundRetirementSourceForm = ({ nextPath, onModalClose }) => {
  const { t } = useTranslation();
  // eslint-disable-next-line no-unused-vars
  const { onSuccess } = useStepPost({ nextPath, onModalClose });
  const fundingSources = useSelector(selectFundingSource);
  const fundingPurposeSources = useSelector(selectFundingPurposeSource);
  const [formDefaultValues, setFormDefaultValues] = useState({});
  const ApplicantData = useApplicantData();
  const Application = useSelector(selectApplication);
  const {
    applicationFundingSourceDispatch,
  } = useApplicationFundingSourceDispatch();
  const dispatch = useDispatch();
  const { gtmStepEvent } = useGtmStepHook();
  const accountGtmType = retrieveFromSessionStorage(ACCOUNT_TYPE_GTM);

  useEffect(() => {
    if (Application && !isEmpty(Application)) {
      const {
        IRAApplication: {
          CustomerDueDiligenceResponse: { IRAAccountFundingInfoResponse },
        },
      } = Application;
      // eslint-disable-next-line no-unused-vars
      const existingAccountPurpose =
        IRAAccountFundingInfoResponse?.AccountPurpose;
      const existingInitialFundingSourceTypes =
        IRAAccountFundingInfoResponse?.InitialFundingSourceTypes;
      const existingInitialFundingOtherDetails =
        IRAAccountFundingInfoResponse?.InitialFundingOtherDetails;
      const existingOngoingFundingSourceTypes =
        IRAAccountFundingInfoResponse?.OngoingFundingSourceTypes;
      const existingOngoingFundingOtherDetails =
        IRAAccountFundingInfoResponse?.OngoingFundingOtherDetails;

      const defaultValues = {
        AccountPurpose: existingAccountPurpose ?? null,
        InitialFundingSourceTypes:
          existingInitialFundingSourceTypes?.map(
            (item) => item.FundingSourceTypeID,
          ) || [],
        InitialFundingOtherDetails: existingInitialFundingOtherDetails || '',
        OngoingFundingSourceTypes:
          existingOngoingFundingSourceTypes?.map(
            (item) => item.FundingSourceTypeID,
          ) || [],
        OngoingFundingOtherDetails: existingOngoingFundingOtherDetails || '',
      };

      setFormDefaultValues(defaultValues);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [Application]);

  useEffect(() => {
    if (fundingSources.length === 0) {
      dispatch(getFundingSource());
    }
  }, [fundingSources, dispatch]);

  const isPersonAddressEmpty = (address) =>
    address && Object.values(address).every((value) => value === null);

  useEffect(() => {
    gtmStepHelper({
      gtmStepEvent,
      eventName: ACCOUNT_STEP_VIEW,
      stepLabel: 'Financial Profile Review',
      accountType: accountGtmType,
      ...(isEmpty(ApplicantData?.PersonName?.FirstName)
        ? {}
        : { accountName: ApplicantData?.PersonName }),
      ...(isEmpty(ApplicantData?.PersonBirthday?.DateOfBirth)
        ? {}
        : { accountDateOfBirth: ApplicantData?.PersonBirthday }),
      ...(isEmpty(ApplicantData?.Phone?.LocalNumber)
        ? {}
        : { accountPhone: ApplicantData?.Phone }),
      ...(isPersonAddressEmpty(ApplicantData?.PersonAddress)
        ? {}
        : { accountAddress: ApplicantData?.PersonAddress }),
      customParams: {
        timestamp: new Date().toISOString(),
      },
    });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const handleGtmEvent = () => {
    gtmStepHelper({
      gtmStepEvent,
      eventName: ACCOUNT_STEP_COMPLETE,
      stepLabel: 'Financial Profile Review',
      accountType: accountGtmType,
      ...(isEmpty(ApplicantData?.PersonName?.FirstName)
        ? {}
        : { accountName: ApplicantData?.PersonName }),
      ...(isEmpty(ApplicantData?.PersonBirthday?.DateOfBirth)
        ? {}
        : { accountDateOfBirth: ApplicantData?.PersonBirthday }),
      ...(isEmpty(ApplicantData?.Phone?.LocalNumber)
        ? {}
        : { accountPhone: ApplicantData?.Phone }),
      ...(isPersonAddressEmpty(ApplicantData?.PersonAddress)
        ? {}
        : { accountAddress: ApplicantData?.PersonAddress }),
      customParams: {
        timestamp: new Date().toISOString(),
      },
    });
  };
  const onRequestSuccess = () => {
    handleGtmEvent();
    onSuccess();
  };
  useEffect(() => {
    if (fundingPurposeSources.length === 0) {
      dispatch(getFundingPurpose());
    }
  }, [fundingPurposeSources, dispatch]);

  useEffect(() => {
    document.body.style.overflow = 'unset';
  }, []); // eslint-disable-line

  const setNextWordLowerCase = (caption) =>
    caption
      .split(' ')
      .map((word, index) => (index === 0 ? word : word.toLowerCase()))
      .join(' ');

  const FundSourceOptions = fundingSources.map(
    ({ FundingTypeID, Caption }) => ({
      value: FundingTypeID,
      label: setNextWordLowerCase(Caption),
    }),
  );

  const personalGoalsOptions = fundingPurposeSources.map(
    ({ AccountPurposeTypeID, Caption }) => ({
      value: AccountPurposeTypeID,
      label: Caption,
    }),
  );

  function getOtherId() {
    const otherSource = fundingSources.find(
      (source) => source.Caption === 'Other',
    );
    return otherSource ? otherSource.FundingTypeID : null;
  }

  const formValidationSchemaFinancialProfile = Yup.object({
    ...getValidationSchema({
      fields: [
        'AccountPurpose',
        'InitialFundingSourceTypes',
        'OngoingFundingSourceTypes',
      ],
    }),
    InitialFundingOtherDetails: Yup.string().when('InitialFundingSourceTypes', {
      is: (initialSource) => initialSource.includes(getOtherId()),
      then: Yup.string().required(
        i18n.t('fields.errors.validation.otherSource'),
      ),
      otherwise: Yup.string().notRequired(),
    }),
    OngoingFundingOtherDetails: Yup.string().when('OngoingFundingSourceTypes', {
      is: (ongoingSource) => ongoingSource.includes(getOtherId()),
      then: Yup.string().required(
        i18n.t('fields.errors.validation.otherSource'),
      ),
      otherwise: Yup.string().notRequired(),
    }),
  });

  const getFundingSourceCaptionById = (id) => {
    const source = fundingSources.find((item) => item.FundingTypeID === id);
    return source ? source.Caption : '';
  };

  const { handleSubmit, control, reset, setError } = useForm({
    resolver: yupResolver(formValidationSchemaFinancialProfile),
    formDefaultValues,
  });

  useEffect(() => {
    if (Application && !isEmpty(Application)) {
      reset({ ...formDefaultValues });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [Application, reset]);

  const onSubmit = ({
    // eslint-disable-next-line no-unused-vars
    AccountPurpose,
    InitialFundingSourceTypes,
    OngoingFundingSourceTypes,
    InitialFundingOtherDetails,
    OngoingFundingOtherDetails,
  }) => {
    const initialFundhasOther = InitialFundingSourceTypes.some(
      (id) => getFundingSourceCaptionById(id) === 'Other',
    );

    const onGoingFundhasOther = OngoingFundingSourceTypes.some(
      (id) => getFundingSourceCaptionById(id) === 'Other',
    );

    const transformedPayload = {
      AccountPurpose: AccountPurpose || 0,
      InitialFundingSourceTypes: InitialFundingSourceTypes.map((id) => ({
        FundingSourceTypeID: id,
        Caption: getFundingSourceCaptionById(id),
      })),
      InitialFundingOtherDetails: initialFundhasOther
        ? InitialFundingOtherDetails
        : '',
      OngoingFundingSourceTypes: OngoingFundingSourceTypes.map((id) => ({
        FundingSourceTypeID: id,
        Caption: getFundingSourceCaptionById(id),
      })),
      OngoingFundingOtherDetails: onGoingFundhasOther
        ? OngoingFundingOtherDetails
        : '',
    };
    const transformedFormDefaultValues = {
      AccountPurpose: formDefaultValues.AccountPurpose,
      InitialFundingSourceTypes: formDefaultValues.InitialFundingSourceTypes.map(
        (id) => ({
          FundingSourceTypeID: id,
          Caption: getFundingSourceCaptionById(id),
        }),
      ),
      InitialFundingOtherDetails: initialFundhasOther
        ? formDefaultValues.InitialFundingOtherDetails
        : '',
      OngoingFundingSourceTypes: formDefaultValues.OngoingFundingSourceTypes.map(
        (id) => ({
          FundingSourceTypeID: id,
          Caption: getFundingSourceCaptionById(id),
        }),
      ),
      OngoingFundingOtherDetails: onGoingFundhasOther
        ? formDefaultValues.OngoingFundingOtherDetails
        : '',
    };
    applicationFundingSourceDispatch({
      data: transformedPayload,
      defaultValues: transformedFormDefaultValues,
      onSuccess: onRequestSuccess,
      onError: (error) => {
        setError(error.field, {
          type: error.type,
          message: error.message,
        });
      },
    });
  };
  const fundSourceIntro = (
    <>
      What will be the{' '}
      <span style={{ textDecoration: 'underline' }}>initial</span> source of
      funds?
    </>
  );

  const fundOngoingIntro = (
    <>
      What will be the{' '}
      <span style={{ textDecoration: 'underline' }}>ongoing</span> source of
      funds?
    </>
  );
  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
    }
  };

  return (
    <>
      <HeadingSubtitle mb={12} color={themeColors.colorTextSecondary}>
        {t('financialProfileSetup.profileTitle')}
      </HeadingSubtitle>
      <Paragraph mb={32} color={themeColors.colorTextXLLight} weight={400}>
        {t('financialProfileSetup.profileIntro')}
      </Paragraph>
      <FormWrap>
        <Paragraph
          mb={5}
          color={themeColors.colorTextSecondary}
          fontWeight={600}
        >
          {t('financialProfileSetup.personalGoalsDesc')}
        </Paragraph>
        <Controller
          control={control}
          render={({ field: { onChange, value }, fieldState }) => (
            <>
              {personalGoalsOptions.map(({ value: key, label }) => (
                <Radio
                  key={key}
                  label={label}
                  selected={value === key}
                  onClick={() => onChange(key)}
                  dataCy={`radio-${key}`}
                />
              ))}
              {fieldState?.error && (
                <ErrorMessage>{fieldState.error.message}</ErrorMessage>
              )}
            </>
          )}
          name="AccountPurpose"
          defaultValue={formDefaultValues?.AccountPurpose || ''}
        />
        <Paragraph
          mb={5}
          mt={20}
          color={themeColors.colorTextSecondary}
          fontWeight={600}
        >
          {fundSourceIntro}
        </Paragraph>
        <Controller
          control={control}
          render={({ field: { onChange, value = [] }, fieldState }) => (
            <>
              {FundSourceOptions &&
                FundSourceOptions.map(({ value: key, label }) => (
                  <Checkbox
                    key={key}
                    label={label}
                    value={value.includes(key)}
                    onClick={() => {
                      const newValue = value.includes(key)
                        ? value.filter((item) => item !== key)
                        : [...value, key];
                      onChange(newValue);
                    }}
                    dataCy={`checkbox-${key}-initialfund`}
                  />
                ))}
              {fieldState?.error && (
                <ErrorMessage>{fieldState.error.message}</ErrorMessage>
              )}
              {value.includes(
                FundSourceOptions.find((option) => option.label === 'Other')
                  ?.value,
              ) && (
                <Controller
                  control={control}
                  render={({ field, fieldState }) => (
                    <InputText
                      label={t(
                        'financialProfileSetup.fundInitialSourceOtherIntro',
                      )}
                      {...field}
                      {...fieldState}
                      autoFocus
                      data-cy="input-initial-source"
                      onKeyDown={handleKeyDown}
                    />
                  )}
                  name="InitialFundingOtherDetails"
                />
              )}
            </>
          )}
          name="InitialFundingSourceTypes"
          defaultValue={formDefaultValues.InitialFundingSourceTypes}
        />
        <Paragraph
          mb={5}
          mt={20}
          color={themeColors.colorTextSecondary}
          fontWeight={600}
        >
          {fundOngoingIntro}
        </Paragraph>
        <Controller
          control={control}
          render={({ field: { onChange, value = [] }, fieldState }) => (
            <>
              {FundSourceOptions &&
                FundSourceOptions.map(({ value: key, label }) => (
                  <Checkbox
                    key={key}
                    label={label}
                    onClick={() => {
                      const newValue = value.includes(key)
                        ? value.filter((item) => item !== key)
                        : [...value, key];
                      onChange(newValue);
                    }}
                    value={value.includes(key)}
                    dataCy={`checkbox-${key}-fund`}
                  />
                ))}
              {fieldState?.error && (
                <ErrorMessage>{fieldState.error.message}</ErrorMessage>
              )}
              {value.includes(
                FundSourceOptions.find((option) => option.label === 'Other')
                  ?.value,
              ) && (
                <Controller
                  control={control}
                  render={({ field, fieldState }) => (
                    <InputText
                      label={t(
                        'financialProfileSetup.fundInitialSourceOtherIntro',
                      )}
                      {...field}
                      {...fieldState}
                      onKeyDown={handleKeyDown}
                      autoFocus
                      data-cy="input-outgoing-source"
                    />
                  )}
                  name="OngoingFundingOtherDetails"
                />
              )}
            </>
          )}
          name="OngoingFundingSourceTypes"
          defaultValue={formDefaultValues.OngoingFundingSourceTypes}
        />
        {/* {error && error && (
          <div style={{ marginTop: '10px' }}>
            <ErrorMessage>{error}</ErrorMessage>
          </div>
        )} */}

        <OnBoardingCta
          type="button"
          onClick={handleSubmit(onSubmit)}
          title={t('continue')}
          buttonProps={{
            'data-cy': 'button-continue',
          }}
        />
      </FormWrap>
    </>
  );
};

FundRetirementSourceForm.propTypes = {
  onModalClose: PropTypes.func,
  nextPath: PropTypes.string,
  handleBack: PropTypes.func,
};

export default FundRetirementSourceForm;
